/**
 * @module CRS
 *
 * Data model for the GeoJSON coordinate reference system (CRS). The GeoJSON
 * 2008 specification declared the coordinate system through a `crs` member,
 * most commonly in the named form:
 *
 * ```json
 * { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4326" } }
 * ```
 *
 * Files in the wild declare it in many other notations too (`"EPSG:4326"`,
 * a bare `4326`, OGC URIs, legacy GML links, ...). This model captures the
 * named CRS structure and knows how to deserialize any of those notations by
 * reusing the parsing helpers in {@link module:DragAndDropGeoJSON}.
 */
import {detectGeoJsonEpsg, parseEpsgValue} from '../interaction/geojson';

/**
 * Standard GeoJSON named CRS object,
 * e.g. `{type: 'name', properties: {name: 'urn:ogc:def:crs:EPSG::4326'}}`.
 */
export type NamedCRS = {
    type: 'name';
    properties: {
        name: string;
    };
};

/**
 * Model of a GeoJSON coordinate reference system, normalized to an EPSG code.
 */
export class GeoJSONCRS {
    /** EPSG numeric code, e.g. `4326`. */
    public readonly code: number;

    /**
     * @param code - EPSG numeric code, e.g. `4326`
     */
    constructor(code: number) {
        this.code = code;
    }

    /** Normalized OpenLayers/OGC code, e.g. `'EPSG:4326'`. */
    public get epsg(): string {
        return 'EPSG:' + this.code;
    }

    /** Named CRS identifier, e.g. `'urn:ogc:def:crs:EPSG::4326'`. */
    public get name(): string {
        return 'urn:ogc:def:crs:EPSG::' + this.code;
    }

    /** Serialize to the standard GeoJSON named CRS object. */
    public toJSON(): NamedCRS {
        return {type: 'name', properties: {name: this.name}};
    }

    /**
     * Deserialize a single CRS identifier value in any supported notation
     * (number, numeric string, `'EPSG:xxxx'`, OGC URN/URI, legacy GML link).
     * @param value - CRS identifier in any of the supported notations
     * @returns the CRS model, or undefined when the value is not recognized
     */
    public static parse(value: unknown): GeoJSONCRS | undefined {
        return GeoJSONCRS.fromEpsg(parseEpsgValue(value));
    }

    /**
     * Deserialize the `crs` member of a GeoJSON object. Understands the named,
     * EPSG and link CRS shapes as well as the non-standard string/number
     * shortcuts.
     * @param geojson - parsed GeoJSON object
     * @returns the CRS model, or undefined when nothing recognizable is declared
     */
    public static fromGeoJSON(geojson: unknown): GeoJSONCRS | undefined {
        return GeoJSONCRS.fromEpsg(detectGeoJsonEpsg(geojson));
    }

    /**
     * Build the model from a normalized `'EPSG:xxxx'` code.
     * @param epsg - normalized EPSG code produced by the parsing helpers
     */
    private static fromEpsg(epsg: string | undefined): GeoJSONCRS | undefined {
        if (!epsg) {
            return undefined;
        }
        const code = Number(epsg.replace('EPSG:', ''));
        return Number.isFinite(code) ? new GeoJSONCRS(code) : undefined;
    }
}

export default GeoJSONCRS;
